// ──────────────────────────────────────────────────────────────────────────────
// Single source of truth for agent identity data across the TRF frontend.
// Every map that used to be scattered across constants.ts, conversation-review
// constants.ts, and types.ts is now derived from AGENT_REGISTRY below.
// ──────────────────────────────────────────────────────────────────────────────

import { COMPANION_NAME, COPILOT_NAME } from './companion-config';

export interface AgentEntry {
  /** Canonical agent id stored in the database (e.g. "clio") */
  id: string;
  /** User-facing persona name — the platform companion (e.g. "Maya", or family override); Maya for the staff copilot */
  shortName: string;
  /** Human-readable internal name shown to admins */
  internalName: string;
  /** Admin display name (used in conversation-review UI) */
  displayName: string;
  /** Tailwind badge color classes */
  colorClass: string;
  /**
   * Service decommissioned. Kept in the registry so historical conversations
   * still render a labelled badge, but excluded from live filter choices.
   */
  retired?: boolean;
}

/** Canonical agent definitions — add new agents here. */
export const AGENT_REGISTRY: AgentEntry[] = [
  {
    id: 'clio',
    shortName: COMPANION_NAME,
    internalName: 'Clio',
    displayName: 'Clio (Chat)',
    colorClass:
      'bg-indigo-100 text-indigo-700 border-indigo-200 dark:bg-indigo-950 dark:text-indigo-300 dark:border-indigo-800',
  },
  {
    id: 'guardian',
    shortName: COMPANION_NAME,
    internalName: 'Guardian',
    displayName: 'Guardian',
    colorClass:
      'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-950 dark:text-rose-300 dark:border-rose-800',
  },
  {
    id: 'circlebot',
    shortName: COMPANION_NAME,
    internalName: 'CircleBot',
    displayName: 'CircleBot',
    retired: true, // decommissioned 2026-06 (#803); history-only
    colorClass:
      'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800',
  },
  {
    id: 'financebot',
    shortName: COMPANION_NAME,
    internalName: 'FinanceBot',
    displayName: 'FinanceBot',
    colorClass:
      'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800',
  },
  {
    id: 'williams',
    shortName: COPILOT_NAME,
    internalName: 'Williams',
    displayName: 'Williams (TRFer)',
    colorClass:
      'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-950 dark:text-slate-300 dark:border-slate-800',
  },
  {
    id: 'calliope',
    shortName: COMPANION_NAME,
    internalName: 'Calliope',
    displayName: 'Calliope (Voice)',
    colorClass:
      'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800',
  },
  {
    id: 'ledger',
    shortName: 'Ledger',
    internalName: 'Ledger',
    displayName: 'Ledger (Dev log)',
    colorClass:
      'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800',
  },
  {
    id: 'marlow',
    shortName: 'Marlow',
    internalName: 'Marlow',
    displayName: 'Marlow (Ledger persona)',
    colorClass:
      'bg-cyan-100 text-cyan-700 border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800',
  },
  {
    id: 'system',
    shortName: COMPANION_NAME,
    internalName: 'System',
    displayName: 'System',
    colorClass:
      'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-700',
  },
];

// ── Aliases ──────────────────────────────────────────────────────────────────
// Deprecated / backward-compat keys that still appear in historical data.

export const AGENT_ALIASES: Record<string, string> = {
  stevens_core: 'clio',
  stevens: 'clio',
  trf_voice: 'calliope',
};

/** Resolve a raw agent id (which may be an alias) to its canonical id. */
export function normalizeAgentId(raw: string): string {
  return AGENT_ALIASES[raw] ?? raw;
}

// ── Derived look-up maps (backward-compatible exports) ───────────────────────

function buildMap<V>(pick: (e: AgentEntry) => V): Record<string, V> {
  const map: Record<string, V> = {};
  for (const entry of AGENT_REGISTRY) {
    map[entry.id] = pick(entry);
  }
  // Add alias entries so legacy look-ups still work without normalizeAgentId.
  for (const [alias, canonical] of Object.entries(AGENT_ALIASES)) {
    const entry = AGENT_REGISTRY.find((e) => e.id === canonical);
    if (entry) map[alias] = pick(entry);
  }
  return map;
}

/** Parent-facing persona names (platform companion name). */
export const AGENT_NAMES: Record<string, string> = buildMap((e) => e.shortName);

/** Internal agent names shown to admin users in debug views. */
export const AGENT_INTERNAL_NAMES: Record<string, string> = buildMap((e) => e.internalName);

/** Admin display names used in conversation-review UI. */
export const AGENT_DISPLAY_NAMES: Record<string, string> = buildMap((e) => e.displayName);

/** Tailwind badge color classes keyed by agent id. */
export const AGENT_COLORS: Record<string, string> = buildMap((e) => e.colorClass);

/** Agent filter options for dropdowns — canonical, non-retired ids only. */
export const AGENT_FILTER_OPTIONS = [
  { value: '', label: 'All agents' },
  ...AGENT_REGISTRY
    .filter((e) => e.id !== 'system' && !e.retired)
    .map((e) => ({ value: e.id, label: e.displayName })),
];
